import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { CheckCircle, Key, Loader2, Lock, Mail } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";

import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Form, FormControl, FormField, FormItem, FormMessage } from "../components/ui/form";
import { Input } from "../components/ui/input";
import { BackButton } from "../components/back-button";

const formSchema = z
	.object({
		email: z.string().email({ message: "Please enter a valid email address." }).optional(),
		otp: z.string().optional(),
		newPassword: z.string().optional(),
		confirmPassword: z.string().optional(),
	})
	.superRefine((data, ctx) => {
		if (data.newPassword && data.confirmPassword && data.newPassword !== data.confirmPassword) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: "Passwords do not match.",
				path: ["confirmPassword"],
			});
		}
	});

enum ResetStage {
	VERIFICATION = "verification",
	OTP = "otp",
	PASSWORD = "password",
}

export function ForgotPassword() {
	const navigate = useNavigate();

	const [stage, setStage] = useState<ResetStage>(ResetStage.VERIFICATION);
	const [email, setEmail] = useState<string>();

	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			email: "",
			otp: "",
			newPassword: "",
			confirmPassword: "",
		},
		mode: "onChange",
	});

	const requestOtpMutation = useMutation({
		mutationFn: async (values: { email: string }) => {
			const data: any = await api.post("api/v1/auth/request-otp", values);

			if (!data.success) {
				throw new Error(data.message ?? "Some error occured while requesting OTP.");
			}

			return data;
		},
		onSuccess: (response: any, variables) => {
			form.resetField("email");

			setEmail(variables.email);
			setStage(ResetStage.OTP);
			toast.success(response.message || `OTP sent to ${variables.email}.`);
		},
		onError: (err) => {
			toast.error(err.message);
		},
	});

	const verifyOtpMutation = useMutation({
		mutationFn: async (values: { otp: string }) => {
			const data: any = await api.post("api/v1/auth/verify-otp", {
				email,
				otp: values.otp,
			});

			if (!data.success) {
				throw new Error(data.message ?? "Invalid OTP.");
			}

			return data;
		},
		onSuccess: (response: any) => {
			form.resetField("otp");

			toast.success(response.message || "OTP verified successfully!");
			setStage(ResetStage.PASSWORD);
		},
		onError: (err) => {
			toast.error(err.message);
		},
	});

	const resetPasswordMutation = useMutation({
		mutationFn: async (values: { newPassword: string; confirmPassword: string }) => {
			const data: any = await api.post("api/v1/auth/forgot-password", {
				email,
				password: values.newPassword,
			});

			if (!data.success) {
				throw new Error(data.message ?? "Failed to reset password.");
			}
			return data;
		},
		onSuccess: (response: any) => {
			toast.success(response.message || "Your password has been reset successfully!");
			form.reset();
			navigate("/login");
		},
		onError: (err) => {
			toast.error(err.message);
		},
	});

	const onSubmit = async (values: z.infer<typeof formSchema>) => {
		switch (stage) {
			case ResetStage.VERIFICATION:
				const isValidEmail = await form.trigger("email");

				if (isValidEmail) {
					requestOtpMutation.mutate({ email: values.email });
				}
				break;
			case ResetStage.OTP:
				const isValidOtp = await form.trigger("otp");

				if (isValidOtp) {
					verifyOtpMutation.mutate({ otp: values.otp });
				}
				break;
			case ResetStage.PASSWORD:
				const isValidPassword = await form.trigger(["newPassword", "confirmPassword"]);

				if (isValidPassword) {
					resetPasswordMutation.mutate({
						newPassword: values.newPassword,
						confirmPassword: values.confirmPassword,
					});
				}
				break;
			default:
				break;
		}
	};

	const isLoading = requestOtpMutation.isPending || verifyOtpMutation.isPending || resetPasswordMutation.isPending;

	const renderStepContent = () => {
		switch (stage) {
			case ResetStage.VERIFICATION:
				return (
					<div className="flex flex-col gap-4 content-stretch mt-3">
						<div className="self-stretch">
							<h2 className="text-lg font-bold">Password Recovery</h2>
							<p className="text-gray-600 text-sm">
								Enter the email address associated with your LegalGraph account to continue
							</p>
						</div>

						<FormField
							control={form.control}
							name="email"
							render={({ field }) => (
								<FormItem className="gap-1">
									<FormControl>
										<div className="relative rounded-md shadow-sm">
											<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
												<Mail className="h-5 w-5 text-gray-400" aria-hidden="true" />
											</div>
											<Input
												{...field}
												type="email"
												placeholder="Enter your email"
												className="pl-10"
												disabled={isLoading}
											/>
										</div>
									</FormControl>
									<FormMessage className="text-xs" />
								</FormItem>
							)}
						/>

						<Button type="submit" disabled={isLoading}>
							{isLoading ? (
								<>
									<Loader2 className="size-5 animate-spin mr-2" />
									Requesting OTP...
								</>
							) : (
								<>
									<Mail className="h-5 w-5 mr-2" />
									Request OTP
								</>
							)}
						</Button>
					</div>
				);
			case ResetStage.OTP:
				return (
					<div className="flex flex-col gap-4 content-stretch mt-3">
						<div className="self-stretch">
							<h2 className="text-lg font-bold">Verify OTP</h2>
							<p className="text-gray-600 text-sm">
								A 6-digit code has been sent to <span className="font-medium text-blue-600">{email}</span>.
							</p>
						</div>

						<FormField
							control={form.control}
							name="otp"
							render={({ field }) => (
								<FormItem className="gap-1">
									<FormControl>
										<div className="relative rounded-md shadow-sm">
											<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
												<Key className="size-5 text-gray-400" aria-hidden="true" />
											</div>
											<Input
												{...field}
												type="text"
												placeholder="Enter 6-digit OTP"
												maxLength={6}
												className="tracking-widest text-center pl-10"
												disabled={isLoading}
											/>
										</div>
									</FormControl>
									<FormMessage className="text-xs" />
								</FormItem>
							)}
						/>

						<Button type="submit" disabled={isLoading}>
							{isLoading ? (
								<>
									<Loader2 className="size-5 animate-spin mr-2" />
									Verifying...
								</>
							) : (
								<>
									<CheckCircle className="size-5 mr-2" />
									Verify OTP
								</>
							)}
						</Button>

						<Button
							variant="secondary"
							onClick={() => {
								setStage(ResetStage.OTP);
								form.reset({ email: email, otp: "", newPassword: "", confirmPassword: "" });
							}}
							disabled={isLoading}
						>
							Resend OTP
						</Button>
					</div>
				);
			case ResetStage.PASSWORD:
				return (
					<div className="flex flex-col gap-4 content-stretch mt-3">
						<div className="self-stretch">
							<h2 className="text-lg font-bold">Enter a new password</h2>
							<p className="text-gray-600 text-sm">Set your new password</p>
						</div>

						<FormField
							control={form.control}
							name="newPassword"
							render={({ field }) => (
								<FormItem className="gap-1">
									<FormControl>
										<div className="relative rounded-md shadow-sm">
											<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
												<Lock className="h-5 w-5 text-gray-400" aria-hidden="true" />
											</div>
											<Input
												{...field}
												type="password"
												placeholder="New Password"
												className="pl-10"
												disabled={isLoading}
											/>
										</div>
									</FormControl>
									<FormMessage className="text-xs" />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="confirmPassword"
							render={({ field }) => (
								<FormItem className="gap-1">
									<FormControl>
										<div className="relative rounded-md shadow-sm">
											<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
												<Lock className="h-5 w-5 text-gray-400" aria-hidden="true" />
											</div>
											<Input
												{...field}
												type="password"
												placeholder="Confirm Password"
												className="pl-10"
												disabled={isLoading}
											/>
										</div>
									</FormControl>
									<FormMessage className="text-xs" />
								</FormItem>
							)}
						/>
						<Button type="submit" disabled={isLoading}>
							{isLoading ? (
								<>
									<Loader2 className="size-5 animate-spin mr-2" />
									Resetting Password...
								</>
							) : (
								<>
									<Lock className="size-5 mr-2" />
									Reset Password
								</>
							)}
						</Button>
					</div>
				);
			default:
				return null;
		}
	};

	return (
		<div className="py-4 px-4">
			<div className="flex flex-col content-stretch">
				<div className="self-stretch">
					<BackButton />
				</div>

				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-3 self-stretch">
						{renderStepContent()}
					</form>
				</Form>
			</div>
		</div>
	);
}
